type BIWidgetSlotOverride = namaTypes.BIWidgetSlotOverride;

// Slot shape per BIDataMappingType for runtime slot selection on wizard-backed widgets.
// Custom and Raw remain omitted — they have no structured slot model.

export type BIMappingSlotKind = 'dimension' | 'measure';

export interface BIMappingSlot {
  /** Canonical slot id used in i18n + component keys. */
  slotId: 'category' | 'label' | 'value' | 'x' | 'y' | 'size' | 'innerLabel' | 'outerLabel' | 'innerValue' | 'outerValue' | 'leftValue' | 'rightValue';
  /** Which field on BIWidgetSlotOverride this slot writes to. */
  wizardFieldIdKey: keyof BIWidgetSlotOverride;
  kind: BIMappingSlotKind;
  /** Translator key for the slot's display label. */
  labelKey: string;
  optional?: boolean;
}

export interface BIMappingTypeShape {
  /** Fixed slots (dimensions + single-measure slots). Empty for mapping types driven entirely by series[]. */
  slots: BIMappingSlot[];
  /**
   * True when the mapping type uses a variable-length series[] where each entry is one measure.
   * Runtime UI shows a multi-select measure picker in that case (only CategoryValue in v1,
   * and only when the existing series config is flexible — see isFlexibleCategoryValueSeries).
   */
  supportsMultiMeasureSeries: boolean;
}

export const BI_MAPPING_TYPE_SHAPES: Record<string, BIMappingTypeShape> = {
  CategoryValue: {
    slots: [{slotId: 'category', wizardFieldIdKey: 'categoryWizardFieldId', kind: 'dimension', labelKey: 'biSlotCategory'}],
    supportsMultiMeasureSeries: true,
  },
  LabelValue: {
    slots: [
      {slotId: 'label', wizardFieldIdKey: 'labelWizardFieldId', kind: 'dimension', labelKey: 'biSlotLabel'},
      {slotId: 'value', wizardFieldIdKey: 'valueWizardFieldId', kind: 'measure', labelKey: 'biSlotValue'},
    ],
    supportsMultiMeasureSeries: false,
  },
  CategoryLabelValue: {
    slots: [
      {slotId: 'category', wizardFieldIdKey: 'categoryWizardFieldId', kind: 'dimension', labelKey: 'biSlotCategory'},
      {slotId: 'label', wizardFieldIdKey: 'labelWizardFieldId', kind: 'dimension', labelKey: 'biSlotLabel'},
      {slotId: 'value', wizardFieldIdKey: 'valueWizardFieldId', kind: 'measure', labelKey: 'biSlotValue'},
    ],
    supportsMultiMeasureSeries: false,
  },
  Scatter: {
    slots: [
      {slotId: 'x', wizardFieldIdKey: 'xWizardFieldId', kind: 'measure', labelKey: 'biSlotX'},
      {slotId: 'y', wizardFieldIdKey: 'yWizardFieldId', kind: 'measure', labelKey: 'biSlotY'},
      {slotId: 'size', wizardFieldIdKey: 'sizeWizardFieldId', kind: 'measure', labelKey: 'biSlotSize', optional: true},
    ],
    supportsMultiMeasureSeries: false,
  },
  Heatmap: {
    slots: [
      {slotId: 'x', wizardFieldIdKey: 'xWizardFieldId', kind: 'dimension', labelKey: 'biSlotX'},
      {slotId: 'y', wizardFieldIdKey: 'yWizardFieldId', kind: 'dimension', labelKey: 'biSlotY'},
      {slotId: 'value', wizardFieldIdKey: 'valueWizardFieldId', kind: 'measure', labelKey: 'biSlotValue'},
    ],
    supportsMultiMeasureSeries: false,
  },
  Gauge: {
    slots: [{slotId: 'value', wizardFieldIdKey: 'valueWizardFieldId', kind: 'measure', labelKey: 'biSlotValue'}],
    supportsMultiMeasureSeries: false,
  },
  Tree: {
    slots: [
      {slotId: 'label', wizardFieldIdKey: 'labelWizardFieldId', kind: 'dimension', labelKey: 'biSlotLabel'},
      {slotId: 'value', wizardFieldIdKey: 'valueWizardFieldId', kind: 'measure', labelKey: 'biSlotValue'},
    ],
    supportsMultiMeasureSeries: false,
  },
  Waterfall: {
    slots: [
      {slotId: 'category', wizardFieldIdKey: 'categoryWizardFieldId', kind: 'dimension', labelKey: 'biSlotCategory'},
      {slotId: 'value', wizardFieldIdKey: 'valueWizardFieldId', kind: 'measure', labelKey: 'biSlotValue'},
    ],
    supportsMultiMeasureSeries: false,
  },
  Radar: {
    slots: [
      {slotId: 'category', wizardFieldIdKey: 'categoryWizardFieldId', kind: 'dimension', labelKey: 'biSlotCategory'},
    ],
    supportsMultiMeasureSeries: false,
  },
  GaugeMulti: {
    slots: [
      {slotId: 'label', wizardFieldIdKey: 'labelWizardFieldId', kind: 'dimension', labelKey: 'biSlotLabel'},
      {slotId: 'value', wizardFieldIdKey: 'valueWizardFieldId', kind: 'measure', labelKey: 'biSlotValue'},
    ],
    supportsMultiMeasureSeries: false,
  },
  NestedLabelValue: {
    slots: [
      {slotId: 'innerLabel', wizardFieldIdKey: 'innerLabelWizardFieldId', kind: 'dimension', labelKey: 'biSlotInnerLabel'},
      {slotId: 'outerLabel', wizardFieldIdKey: 'outerLabelWizardFieldId', kind: 'dimension', labelKey: 'biSlotOuterLabel'},
      {slotId: 'innerValue', wizardFieldIdKey: 'innerValueWizardFieldId', kind: 'measure', labelKey: 'biSlotInnerValue'},
      {slotId: 'outerValue', wizardFieldIdKey: 'outerValueWizardFieldId', kind: 'measure', labelKey: 'biSlotOuterValue'},
    ],
    supportsMultiMeasureSeries: false,
  },
  FunnelComparison: {
    slots: [
      {slotId: 'label', wizardFieldIdKey: 'labelWizardFieldId', kind: 'dimension', labelKey: 'biSlotLabel'},
      {slotId: 'leftValue', wizardFieldIdKey: 'leftValueWizardFieldId', kind: 'measure', labelKey: 'biSlotLeftValue'},
      {slotId: 'rightValue', wizardFieldIdKey: 'rightValueWizardFieldId', kind: 'measure', labelKey: 'biSlotRightValue'},
    ],
    supportsMultiMeasureSeries: false,
  },
};

export function getMappingTypeShape(type: string | undefined): BIMappingTypeShape | null {
  if (!type) return null;
  return BI_MAPPING_TYPE_SHAPES[type] ?? null;
}

interface CategoryValueSeriesEntry {
  type?: string;
  yAxisIndex?: number | null;
  stack?: string | null;
  target?: number | null;
}

/**
 * Flexible = every series entry is uniform (no dual-axis, no stack, no target, same chart type).
 * Only flexible CategoryValue widgets allow runtime add/remove of measures via multi-select.
 * Fixed-shape CategoryValue (dual-axis, combo-bar-line, line-with-target, etc.) are v2.
 */
export function isFlexibleCategoryValueSeries(series: CategoryValueSeriesEntry[] | undefined | null): boolean {
  if (!series || series.length === 0) return true;
  const firstType = series[0].type;
  return series.every(s =>
    (s.yAxisIndex === null || s.yAxisIndex === undefined) &&
    (s.stack === null || s.stack === undefined) &&
    (s.target === null || s.target === undefined) &&
    (s.type ?? firstType) === firstType
  );
}
